import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useWebSocket } from '../../hooks/useWebSocket';
import { useRunStore } from '../../state/runStore';

export interface AnalyzeProgress {
  current: number;
  total: number;
  completed: number;
  errors: number;
}

/**
 * Per-call override for which evidence blocks the AI sees. Optional - when
 * omitted the server uses whatever exists on disk (images + video together
 * if both are present).
 */
export interface AnalyzeInputs {
  images: boolean;
  video: boolean;
  provider?: 'openai-frames' | 'gemini-native';
}

export interface UseAutoAnalyze {
  running: boolean;
  progress: AnalyzeProgress | null;
  error: string | null;
  start: (onlyMissing: boolean, inputs?: AnalyzeInputs) => Promise<void>;
  stop: () => Promise<void>;
}

interface Options {
  runId: string;
  totalTests: number;
  unanalyzed: number;
}

/**
 * Owns the lifecycle of the bulk "analyze all tests" job: queries initial
 * running state, listens to WebSocket progress events, and exposes start /
 * stop actions. Pulled out of AutoAnalyzeBar so multiple presentations
 * (full card and the Phase 5 toolbar cluster) can share state.
 *
 * Also mirrors `running` into the run store so the phases rail can show a
 * spinner even when no single test is mid-flight (gap between tests).
 */
export function useAutoAnalyze({ runId, totalTests, unanalyzed }: Options): UseAutoAnalyze {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<AnalyzeProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const setTestAnalyzing = useRunStore((s) => s.setTestAnalyzing);
  const clearAnalyzing = useRunStore((s) => s.clearAnalyzing);
  const setScoutProgress = useRunStore((s) => s.setScoutProgress);
  const setAnalyzeAllRunning = useRunStore((s) => s.setAnalyzeAllRunning);

  const markRunning = (next: boolean) => {
    setRunning(next);
    setAnalyzeAllRunning(next);
  };

  useEffect(() => {
    api
      .get<{ running: boolean }>(`/api/runs/${runId}/tests/analyze-all/status`)
      .then((data) => markRunning(data.running))
      .catch(() => undefined);
    return () => {
      // Keep store flag if the job is still going; DashboardLayout owns
      // the cross-phase mirror. Only clear local state on unmount via
      // the status poll on remount.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- markRunning closes over setters
  }, [runId]);

  useEffect(() => {
    setAnalyzeAllRunning(running);
  }, [running, setAnalyzeAllRunning]);

  useWebSocket(runId, (msg) => {
    if (msg.type === 'analyze-progress' && msg.runId === runId) {
      setProgress({ current: msg.current, total: msg.total, completed: msg.completed, errors: msg.errors });
      if (msg.current === msg.total) {
        markRunning(false);
        clearAnalyzing();
      }
    } else if (msg.type === 'phase' && msg.phase === 'analyze-tests' && msg.runId === runId) {
      if (msg.status === 'in_progress') markRunning(true);
      else {
        markRunning(false);
        clearAnalyzing();
      }
    } else if (msg.type === 'test-analyze-status' && msg.runId === runId) {
      setTestAnalyzing(msg.testKey, msg.status === 'started');
      // Drop any lingering scout-progress entry when the aggregator pass
      // finishes (success or failure) - the banner that consumes this
      // state is gated on `analyzing`, but cleaning up here keeps the
      // store small after a long analyze-all run.
      if (msg.status !== 'started') setScoutProgress(msg.testKey, null);
    } else if (msg.type === 'test-scout-progress' && msg.runId === runId) {
      setScoutProgress(msg.testKey, {
        current: msg.current,
        total: msg.total,
        filename: msg.filename,
        status: msg.status,
      });
    }
  });

  const start = async (onlyMissing: boolean, inputs?: AnalyzeInputs) => {
    setError(null);
    markRunning(true);
    setProgress({ current: 0, total: onlyMissing ? unanalyzed : totalTests, completed: 0, errors: 0 });
    try {
      await api.post(`/api/runs/${runId}/tests/analyze-all`, {
        onlyMissing,
        ...(inputs ? { inputs } : {}),
      });
    } catch (err) {
      setError((err as Error).message);
      markRunning(false);
    }
  };

  const stop = async () => {
    try {
      await api.post(`/api/runs/${runId}/tests/analyze-all/stop`);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return { running, progress, error, start, stop };
}
